import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiFileText, FiX } from "react-icons/fi";
import "./ChallanInvoicePopup.css";

const money = (value) => `₹${(Number(value) || 0).toFixed(2)}`;

export default function ChallanInvoicePopup({
  open,
  type = "Challan",
  form = {},
  products = [],
  saving = false,
  onClose,
  onGenerate,
}) {
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [productRates, setProductRates] = useState({});
  const [challanData, setChallanData] = useState(null);

  // Debug logging
  console.log('ChallanInvoicePopup - products:', products);
  console.log('ChallanInvoicePopup - form:', form);

  const displayForm = challanData?.form || form;
  const displayProducts = challanData?.products || products;

  const totals = useMemo(() => {
    const isCombined = type === "COMBINED" || type === "Combined Challan";
    const subtotal = displayProducts.reduce((sum, item) => {
      const itemId = item.id || `${item.name}-${item.type}`;
      const deliveredQty = Number(item.deliveredQty) || 0;
      const receivedQty = Number(item.receivedQty) || 0;
      const qty = Number(item.deliveredQty || item.receivedQty || item.qty || item.quantity) || 0;
      const baseRate = Number(item.rate || item.price || item.unitPrice) || 0;
      const customRate = Number(productRates[itemId]) || 0;
      const rate = customRate > 0 ? customRate : baseRate;

      if (isCombined) {
        return sum + (deliveredQty * rate) + (receivedQty * rate);
      }
      return sum + qty * rate;
    }, 0);
    return { subtotal, tax: 0, total: subtotal };
  }, [displayProducts, type, productRates]);

  useEffect(() => {
    if (!open) return undefined;

    setShowConfirmation(true);
    setChallanData({ form, products });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, saving, onClose, form, products]);

  if (!open) return null;

  const challanNumber = form.challanNumber && form.challanNumber !== "AUTO"
    ? form.challanNumber
    : "AUTO";

  return createPortal(
    showConfirmation ? (
      <div className="cip-overlay cip-confirm-overlay" role="dialog" aria-modal="true" aria-label="Create new invoice">
        <div className="cip-confirm-modal">
          <div className="cip-confirm-icon">
            <FiFileText size={24} />
          </div>
          <h2>Create New Invoice?</h2>
          <p>Would you like to open the invoice details before generating this challan?</p>
          <div className="cip-confirm-actions">
            <button
              type="button"
              className="cip-secondary cip-confirm-btn"
              onClick={onGenerate}
              disabled={saving}
            >
              No, Generate Directly
            </button>
            <button
              type="button"
              className="cip-primary cip-confirm-btn"
              onClick={() => setShowConfirmation(false)}
              disabled={saving}
            >
              Yes, View Details
            </button>
          </div>
        </div>
      </div>
    ) : (
      <div className="cip-overlay" role="dialog" aria-modal="true" aria-label="Generate invoice">
        <div className="cip-modal">
          <div className="cip-header">
            <div className="cip-title-wrap">
              <div className="cip-title-icon"><FiFileText size={19} /></div>
              <div>
                <h2>Generate Invoice</h2>
                <p>Invoice summary for {type}</p>
              </div>
            </div>
            <button type="button" className="cip-close" onClick={onClose} aria-label="Close">
              <FiX size={21} />
            </button>
          </div>

          <div className="cip-body">
            <div className="cip-status">
              <span>CHALLAN TYPE</span>
              <strong>{type}</strong>
            </div>

            <section className="cip-section">
              <div className="cip-section-title">Customer Details</div>
              <div className="cip-grid">
                <Info label="Customer" value={displayForm.customer || "-"} />
                <Info label="Customer ID" value={displayForm.customerId || "-"} />
                <Info label="Site Location" value={displayForm.siteLocation || "-"} />
                <Info label="Invoice Date" value={displayForm.date || "-"} />
                <Info label="Challan Number" value={challanNumber} />
                <Info label="Purchase Order" value={displayForm.poNo || displayForm.purchaseOrderNo || "-"} />
              </div>
            </section>

            <section className="cip-section">
              <div className="cip-section-title">Transport & Delivery</div>
              <div className="cip-grid">
                <Info label="Vehicle Number" value={displayForm.vehicleNo || displayForm.vehicleNumber || "-"} />
                <Info label="Transporter" value={displayForm.transporter || "-"} />
                <Info label="Driver" value={displayForm.driverDetails || displayForm.driverName || "-"} />
                <Info label="Driver Number" value={displayForm.driverNumber || "-"} />
                <Info label="Delivery Details" value={displayForm.deliveryDetails || "-"} wide />
              </div>
            </section>

            <section className="cip-section">
              <div className="cip-section-head">
                <div className="cip-section-title">Invoice Items</div>
                <span className="cip-count">{displayProducts.length} item{displayProducts.length === 1 ? "" : "s"}</span>
              </div>
              <div className="cip-items-wrap">
                <table className="cip-items">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Type</th>
                      {type === "Delivered Challan" || type === "DELIVERED" ? (
                        <th>Delivered Qty</th>
                      ) : type === "Received Challan" || type === "RECEIVED" ? (
                        <th>Received Qty</th>
                      ) : (
                        <>
                          <th>Delivered Qty</th>
                          <th>Received Qty</th>
                        </>
                      )}
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayProducts.length ? displayProducts.map((item, index) => {
                      const itemId = item.id || `${item.name}-${item.type}`;
                      const deliveredQty = Number(item.deliveredQty) || 0;
                      const receivedQty = Number(item.receivedQty) || 0;
                      const qty = Number(item.deliveredQty || item.receivedQty || item.qty || item.quantity) || 0;
                      const baseRate = Number(item.rate || item.price || item.unitPrice) || 0;
                      const customRate = productRates[itemId] || "";
                      const rate = Number(customRate) > 0 ? Number(customRate) : baseRate;
                      const isCombined = type === "COMBINED" || type === "Combined Challan";
                      const isDelivered = type === "Delivered Challan" || type === "DELIVERED";
                      const isReceived = type === "Received Challan" || type === "RECEIVED";
                      const itemTotal = isCombined ? (deliveredQty * rate) + (receivedQty * rate) : qty * rate;

                      return (
                        <tr key={itemId}>
                          <td><strong>{item.name || "Unnamed Item"}</strong></td>
                          <td>{item.type || "-"}</td>
                          {isDelivered ? (
                            <td>{deliveredQty || "-"}</td>
                          ) : isReceived ? (
                            <td>{receivedQty || "-"}</td>
                          ) : (
                            <>
                              <td>{deliveredQty || "-"}</td>
                              <td>{receivedQty || "-"}</td>
                            </>
                          )}
                          <td>
                            <input
                              type="text"
                              value={customRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers and decimal point
                                const numericValue = value.replace(/[^0-9.]/g, '');
                                setProductRates(prev => ({ ...prev, [itemId]: numericValue }));
                              }}
                              placeholder={baseRate > 0 ? baseRate : "Rate"}
                              disabled={saving}
                              style={{
                                width: "80px",
                                padding: "4px 8px",
                                border: "1px solid var(--cip-border)",
                                borderRadius: "4px",
                                fontSize: "12px"
                              }}
                            />
                          </td>
                          <td className="cip-amount">{money(itemTotal)}</td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={type === "COMBINED" || type === "Combined Challan" ? 6 : 5} className="cip-empty">No products added</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="cip-summary">
              <div><span>Subtotal</span><strong>{money(totals.subtotal)}</strong></div>
              <div><span>Tax</span><strong>{money(totals.tax)}</strong></div>
              <div className="cip-total"><span>Total</span><strong>{money(totals.total)}</strong></div>
            </div>

            <section className="cip-section cip-notes">
              <Info label="Deposit / Payment Type" value={displayForm.depositType || "-"} />
              <Info label="Deposit Amount" value={money(displayForm.depositAmount || displayForm.returnedAmount)} />
              <Info label="Notes" value={displayForm.depositNarration || displayForm.depositeNarration || "-"} wide />
            </section>
          </div>

          <div className="cip-footer">
            <button type="button" className="cip-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="cip-primary" onClick={onGenerate} disabled={saving}>
              <FiCheck size={16} />
              {saving ? "Generating..." : "Generate Invoice"}
            </button>
          </div>
        </div>
      </div>
    ),
    document.body
  );
}

function Info({ label, value, wide = false }) {
  return (
    <div className={`cip-info ${wide ? "cip-wide" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
